import { type MouseEvent, memo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { MediaCard } from '@core/components';
import type { Design } from '../../api/design';
import { getImageAspectRatio, getImageThumbnailUrl } from '../utils/image';
import { formatMeasurement } from '../utils/measurement';
import { getDesignChipLabel } from '../utils/vestment';

interface DesignCardProps {
  design: Design;
  priority: boolean; // Loads the image eagerly at high priority, for the first rows.
  selected?: boolean;
  compact?: boolean; // Shows only the code, without the type and center finish chips.
  onOpen: (design: Design) => void;
  onMenu?: (event: MouseEvent<HTMLElement>, design: Design) => void;
}

const DesignCard = ({
  design,
  priority,
  selected,
  compact,
  onOpen,
  onMenu,
}: DesignCardProps) => (
  <MediaCard
    content={
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          maxWidth: '100%',
        }}
      >
        <Typography variant="subtitle2" textAlign="center" fontWeight={600}>
          {design.code}
        </Typography>
        {compact ? null : (
          <>
            <Chip
              size="small"
              label={getDesignChipLabel(design.vestmentType)}
              sx={{ maxWidth: '100%', fontWeight: 600 }}
            />
            {design.centerFinish ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Center Finish: ${formatMeasurement(design.centerFinish)}`}
                sx={{
                  maxWidth: '100%',
                  borderColor: 'action.selected',
                }}
              />
            ) : null}
          </>
        )}
      </Box>
    }
    imageSrc={getImageThumbnailUrl(design)}
    aspectRatio={getImageAspectRatio(design) ?? 3 / 5}
    imageLoading={priority ? 'eager' : 'lazy'}
    fetchPriority={priority ? 'high' : undefined}
    selected={selected}
    onClick={() => onOpen(design)}
    onMenuClick={onMenu ? (event) => onMenu(event, design) : undefined}
  />
);

export default memo(DesignCard);
